// Razorpay payment-ecosystem manifest — the universe of payment instruments
// we surface health for on /admin/provider-status. Mirrors the four columns
// Razorpay's own "Ecosystem Health" dashboard renders.
//
// The shape is intentionally static (not server-driven) for two reasons:
//   1. Razorpay's downtimes API only lists IMPAIRED instruments — the green
//      ones are the universe minus the impaired set. We need to know the
//      universe to render the all-good rows.
//   2. The brand palette + monogram for each instrument is presentation
//      data, not platform data. Storing it in the DB would be over-design.
//
// When a new instrument starts appearing in Razorpay's downtime feed and we
// don't recognise its code, the page surfaces it under "Other" in the
// matching column (see ProviderStatus.tsx fallthrough). That keeps the page
// honest about new outages without needing a panel deploy.

import type { RazorpayDowntime } from "@/api/razorpayDowntimes";

/**
 * Instrument — one row in a column. The `id` is the Razorpay-side code we
 * match downtimes on (bank code for netbanking, issuer/network for cards,
 * vpa_handle for UPI, psp for UPI apps).
 */
export interface Instrument {
  id: string;
  name: string;
  /** Short monogram for the logo tile (1–3 chars). */
  monogram: string;
  /** Background colour for the logo tile. Brand-adjacent, palette-shifted. */
  color: string;
}

/** Named sub-section inside a column (e.g. "VPA handle" vs "PSP apps"). */
export interface InstrumentGroup {
  label: string;
  items: Instrument[];
}

/**
 * MethodColumn — one of the four top-level columns. `match` returns the
 * instrument id from a downtime when this column owns it, or null when
 * the downtime belongs to a different column. This lets us partition the
 * downtimes array into per-column buckets in a single pass.
 */
export interface MethodColumn {
  key: string;
  label: string;
  groups: InstrumentGroup[];
  /** Which Razorpay downtime.method values feed this column. */
  methods: string[];
  /**
   * Project a downtime onto this column's instrument id, or return null if
   * the downtime isn't one of this column's responsibilities.
   */
  match: (d: RazorpayDowntime) => string | null;
}

// ── UPI ───────────────────────────────────────────────────────────────────

const UPI_VPA_HANDLES: Instrument[] = [
  { id: "apl",         name: "APL",          monogram: "AP", color: "#FF9900" },
  { id: "okicici",     name: "okicici",      monogram: "OI", color: "#F37920" },
  { id: "oksbi",       name: "oksbi",        monogram: "SB", color: "#1A6FA8" },
  { id: "okaxis",      name: "okaxis",       monogram: "AX", color: "#97144D" },
  { id: "ybl",         name: "YBL",          monogram: "YB", color: "#1B388A" },
  { id: "okhdfcbank",  name: "okhdfcbank",   monogram: "HD", color: "#004C8F" },
  { id: "ibl",         name: "ibl",          monogram: "IB", color: "#283F88" },
  { id: "upi",         name: "upi",          monogram: "UP", color: "#067655" },
];

const UPI_PSP_APPS: Instrument[] = [
  { id: "amazon_pay",  name: "Amazon Pay",   monogram: "AP", color: "#FF9900" },
  { id: "bhim",        name: "BHIM",         monogram: "BH", color: "#F28100" },
  { id: "paytm",       name: "Paytm",        monogram: "PT", color: "#00B9F1" },
  { id: "google_pay",  name: "Google Pay",   monogram: "GP", color: "#4285F4" },
  { id: "phonepe",     name: "PhonePe",      monogram: "PP", color: "#5F259F" },
];

// ── Cards ─────────────────────────────────────────────────────────────────

const CARD_NETWORKS: Instrument[] = [
  { id: "DINERS",      name: "Diners Club",  monogram: "DC", color: "#0079BE" },
  { id: "VISA",        name: "VISA",         monogram: "V",  color: "#1A1F71" },
  { id: "MC",          name: "Mastercard",   monogram: "MC", color: "#EB001B" },
  { id: "AMEX",        name: "American Express", monogram: "AX", color: "#006FCF" },
  { id: "RUPAY",       name: "RuPay",        monogram: "RP", color: "#097B47" },
];

const CARD_ISSUERS: Instrument[] = [
  { id: "BKID",        name: "Bank Of India",      monogram: "BO", color: "#F89C1C" },
  { id: "CNRB",        name: "Canara Bank",        monogram: "CB", color: "#DAA520" },
  { id: "RATN",        name: "RBL Bank",           monogram: "RB", color: "#F26522" },
  { id: "HSBC",        name: "HSBC Corporation",   monogram: "HS", color: "#DB0011" },
  { id: "ICIC",        name: "ICICI Bank",         monogram: "IC", color: "#F37920" },
  { id: "HDFC",        name: "HDFC Bank",          monogram: "HD", color: "#004C8F" },
  { id: "SBIN",        name: "State Bank Of India", monogram: "SB", color: "#1A6FA8" },
  { id: "UTIB",        name: "Axis Bank",          monogram: "AX", color: "#97144D" },
  { id: "KKBK",        name: "Kotak Mahindra Bank", monogram: "KM", color: "#ED1B23" },
];

// ── Net Banking ───────────────────────────────────────────────────────────

const NETBANKING_BANKS: Instrument[] = [
  { id: "KKBK",        name: "Kotak Mahindra Bank", monogram: "KM", color: "#ED1B23" },
  { id: "ICIC",        name: "ICICI Bank",         monogram: "IC", color: "#F37920" },
  { id: "UTIB",        name: "Axis Bank",          monogram: "AX", color: "#97144D" },
  { id: "HDFC",        name: "HDFC Bank",          monogram: "HD", color: "#004C8F" },
  { id: "SBIN",        name: "State Bank Of India", monogram: "SB", color: "#1A6FA8" },
  { id: "DLXB",        name: "Dhanlaxmi Bank",     monogram: "DL", color: "#0083C9" },
];

// ── E-mandate ─────────────────────────────────────────────────────────────

const EMANDATE_BANKS: Instrument[] = [
  { id: "UTIB",        name: "Axis Bank",          monogram: "AX", color: "#97144D" },
  { id: "HDFC",        name: "HDFC Bank",          monogram: "HD", color: "#004C8F" },
  { id: "ICIC",        name: "ICICI Bank",         monogram: "IC", color: "#F37920" },
  { id: "KKBK",        name: "Kotak Mahindra Bank", monogram: "KM", color: "#ED1B23" },
  { id: "SBIN",        name: "State Bank Of India", monogram: "SB", color: "#1A6FA8" },
  { id: "PYTM",        name: "Paytm Payments Bank", monogram: "PT", color: "#00B9F1" },
];

// ── Column definitions ────────────────────────────────────────────────────

export const COLUMNS: MethodColumn[] = [
  {
    key: "upi",
    label: "UPI",
    methods: ["upi"],
    groups: [
      { label: "VPA handle", items: UPI_VPA_HANDLES },
      { label: "PSP apps",   items: UPI_PSP_APPS },
    ],
    match: (d) => {
      if (d.method !== "upi") return null;
      return d.instrument?.vpa_handle ?? d.instrument?.psp ?? null;
    },
  },
  {
    key: "cards",
    label: "Cards",
    methods: ["card"],
    groups: [
      { label: "Network", items: CARD_NETWORKS },
      { label: "Issuer",  items: CARD_ISSUERS },
    ],
    match: (d) => {
      if (d.method !== "card") return null;
      return d.instrument?.network ?? d.instrument?.issuer ?? null;
    },
  },
  {
    key: "netbanking",
    label: "Net Banking",
    methods: ["netbanking"],
    groups: [
      { label: "Bank", items: NETBANKING_BANKS },
    ],
    match: (d) => {
      if (d.method !== "netbanking") return null;
      return d.instrument?.bank ?? null;
    },
  },
  {
    key: "emandate",
    label: "E-mandate",
    methods: ["emandate"],
    groups: [
      { label: "Bank", items: EMANDATE_BANKS },
    ],
    match: (d) => {
      if (d.method !== "emandate") return null;
      return d.instrument?.bank ?? null;
    },
  },
];

/**
 * findInstrumentName — best-effort display name for a downtime that didn't
 * match any known instrument id. The downtime still surfaces (under
 * "Other") so a new code in Razorpay's feed doesn't disappear from the
 * page, it just renders with the raw id as its name until we add it to
 * the manifest above.
 */
export function findInstrumentName(
  column: MethodColumn,
  id: string,
): string {
  for (const g of column.groups) {
    const hit = g.items.find((i) => i.id === id);
    if (hit) return hit.name;
  }
  return id;
}
